/* Sticky mobile add-to-cart bar.
 *
 * Variant state lives in the main product form and nowhere else. This element
 * drives the theme's existing option selectors and listens to the theme's own
 * `cc-variant-updated` broadcast, so the URL parameter, gallery, price and
 * button state all keep flowing through theme.js exactly as they already do.
 *
 * The theme's product options are wired with jQuery (`change.themeProductOptions`
 * bound to `.option-selectors`), and jQuery's `.trigger('change')` does not
 * dispatch a native event. Writing a change therefore has to go through jQuery
 * too, which is why this file uses the theme's own global.
 */
(function () {
  'use strict';

  var DESKTOP_BREAKPOINT = 960;

  /* Shopify pins an admin bar to the bottom of the viewport when you preview an
   * unpublished theme. It is 68px tall on a phone and sits exactly where this
   * bar does, so in preview the feature looks broken when it is in fact working.
   * Measure it and lift the bar above it.
   *
   * This is inherently production-safe: the element only exists in a preview
   * context, so the custom property is never set on the live storefront and the
   * CSS falls back to 0px.
   */
  function syncPreviewBarOffset() {
    var pv = document.querySelector('#PBarNextFrameWrapper, #PBarNextFrame');
    var height = 0;
    if (pv) {
      var rect = pv.getBoundingClientRect();
      // Only count it when it is actually pinned to the bottom of the viewport.
      if (rect.height > 0 && Math.abs(rect.bottom - window.innerHeight) < 4) {
        height = Math.round(rect.height);
      }
    }
    document.documentElement.style.setProperty('--cc-preview-bar-height', height + 'px');
    return height;
  }

  window.customElements.define(
    'cc-sticky-atc',
    class extends HTMLElement {
      connectedCallback() {
        // The main form is the source of truth for everything this bar shows.
        this.container = this.closest('[data-section-type="main-product"]') || document;
        this.productForm = this.container.querySelector('.product-form');
        this.purchaseForm = this.container.querySelector('form.product-purchase-form');
        this.buyButtonsRow =
          this.container.querySelector('.buy-buttons-row') || this.purchaseForm;
        this.mainPriceArea = this.container.querySelector('.price-container .price-area');

        this.select = this.querySelector('[data-sticky-option]');
        this.addButton = this.querySelector('[data-sticky-add]');
        this.scrollButton = this.querySelector('[data-sticky-scroll]');
        this.priceSlot = this.querySelector('[data-sticky-price]');

        if (!this.productForm || !this.buyButtonsRow) {
          this.remove();
          return;
        }

        this.visible = false;
        this.bindEvents();
        this.observe();

        // The preview bar mounts asynchronously, so measure now and again once
        // it has had a chance to appear.
        syncPreviewBarOffset();
        window.setTimeout(syncPreviewBarOffset, 1200);
        window.addEventListener('resize', this.boundResize = () => syncPreviewBarOffset());
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.boundResize) window.removeEventListener('resize', this.boundResize);
        if (window.jQuery && this.boundVariantUpdated) {
          window.jQuery(window).off('cc-variant-updated', this.boundVariantUpdated);
        }
        document.body.classList.remove('cc-sticky-atc-active');
      }

      bindEvents() {
        if (this.select) {
          this.select.addEventListener('change', this.onSelectChange.bind(this));
        }
        if (this.addButton) {
          this.addButton.addEventListener('click', this.onAdd.bind(this));
        }
        if (this.scrollButton) {
          this.scrollButton.addEventListener('click', this.scrollToForm.bind(this));
        }

        // Main form -> bar. theme.js broadcasts this on every variant change.
        if (window.jQuery) {
          this.boundVariantUpdated = (evt, data) => this.onVariantUpdated(data);
          window.jQuery(window).on('cc-variant-updated', this.boundVariantUpdated);
        }
      }

      observe() {
        // IntersectionObserver only. No scroll listeners.
        //
        // The root is extended a long way below the viewport, so the observed
        // element counts as intersecting whenever any part of it is at or below
        // the viewport top. "Not intersecting" therefore means exactly one
        // thing: the buy button has scrolled entirely off the top.
        //
        // A plain threshold-0 observer cannot express this. It reports only
        // crossings, so jumping straight from below-the-fold to scrolled-past
        // (anchor links, restored scroll position, a fast flick) never enters
        // the intersecting state and no callback is delivered at all. Framing
        // it as "entirely above the top" or not makes every such jump a real
        // state change that the observer reliably reports.
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => this.toggle(!entry.isIntersecting));
          },
          { threshold: 0, rootMargin: '0px 0px 100000px 0px' }
        );
        this.observer.observe(this.buyButtonsRow);
      }

      toggle(show) {
        if (show === this.visible) return;
        if (show && window.innerWidth >= DESKTOP_BREAKPOINT) return;

        this.visible = show;
        this.classList.toggle('cc-sticky-atc--visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.body.classList.toggle('cc-sticky-atc-active', show);

        if (show) {
          document.documentElement.style.setProperty(
            '--cc-sticky-atc-height',
            this.offsetHeight + 'px'
          );
        }
      }

      /* ----- bar -> main form ------------------------------------------- */

      onSelectChange() {
        var value = this.select.value;
        if (!value) return;

        var optionSelector = this.container.querySelector(
          '.option-selectors .option-selector[data-option-index="' +
            this.select.dataset.optionIndex +
            '"]'
        );
        if (!optionSelector) return;

        if (optionSelector.dataset.selectorType === 'dropdown') {
          // Custom select component: it exposes a selectOption event.
          var ccSelect = optionSelector.querySelector('.cc-select');
          if (ccSelect) {
            ccSelect.dispatchEvent(
              new CustomEvent('selectOption', { detail: { value: value } })
            );
          }
          return;
        }

        // Listed (radio) style: check the matching input, then let the theme's
        // own handler resolve the variant and update everything downstream.
        var matched = null;
        optionSelector.querySelectorAll('.js-option').forEach((input) => {
          if (input.value === value) matched = input;
        });
        if (!matched) return;

        matched.checked = true;
        if (window.jQuery) {
          window.jQuery(optionSelector).closest('.option-selectors').trigger('change');
        } else {
          matched.dispatchEvent(new CustomEvent('change', { bubbles: true }));
        }
      }

      onAdd() {
        if (this.addButton.hasAttribute('disabled')) return;

        // If a variant still is not selected, send the shopper to the real
        // picker rather than failing silently.
        var idInput = this.purchaseForm && this.purchaseForm.querySelector('[name="id"]');
        if (!idInput || !idInput.value) {
          this.scrollToForm();
          return;
        }

        // Submit the theme's own form so the theme's ajax handler, validation
        // and after-add-to-cart behaviour all apply unchanged. requestSubmit
        // fires a native submit event, which the theme's jQuery handler sees.
        if (typeof this.purchaseForm.requestSubmit === 'function') {
          this.purchaseForm.requestSubmit();
        } else if (window.jQuery) {
          window.jQuery(this.purchaseForm).submit();
        }
      }

      scrollToForm() {
        var target = this.container.querySelector('.option-selectors') || this.buyButtonsRow;
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'center'
        });
        var focusable = target.querySelector('input, select, button');
        if (focusable) {
          window.setTimeout(() => focusable.focus({ preventScroll: true }), 300);
        }
      }

      /* ----- main form -> bar ------------------------------------------- */

      onVariantUpdated(data) {
        var variant = data && data.variant;

        if (this.select && variant && variant.options) {
          var value = variant.options[Number(this.select.dataset.optionIndex)];
          if (value != null && this.select.value !== value) {
            this.select.value = value;
          }
        }

        // Copy the price the theme just rendered rather than formatting money
        // here: one source of truth for currency format and sale styling.
        if (this.priceSlot) {
          var priceArea = this.container.querySelector('.price-container .price-area');
          if (priceArea) this.priceSlot.innerHTML = priceArea.innerHTML;
        }

        if (this.addButton) {
          var unavailable = !variant || !variant.available;
          this.addButton.toggleAttribute('disabled', unavailable);
          this.addButton.classList.toggle('button--sold-out', unavailable);
          if (unavailable && window.theme && theme.strings) {
            this.addButton.textContent = variant
              ? theme.strings.products_variant_no_stock
              : theme.strings.products_variant_non_existent;
          } else if (!unavailable) {
            this.addButton.textContent = this.addButton.dataset.addToCartText;
          }
        }
      }
    }
  );
})();
